import React from 'react';
import {
  Box, Chip, Paper, Stack, Tooltip, Typography,
} from '@mui/material';
import { areaTypeLabel, humanMs } from './areas';
import { attentionText } from './attention';

// Who is standing where, laid out the way the yard is.
//
// A table answers "list me everything", and that is a real question - it sorts, it filters, it
// downloads. It is not the question somebody asks when they walk up to this screen, which is "how
// are the lorries spread across my site". Answering that from rows means reading the Area column
// forty times and holding a tally in your head.
//
// So each area is a column and each lorry a card in it. The distribution is then the shape of the
// screen rather than something to be worked out: a column twice as tall has twice as many lorries
// in it, and the one with red cards at the top is the one to walk to. The list is still there for
// the questions a list is better at.

const Card = ({ row, onOpen }) => {
  const level = row.attention?.level;
  return (
    <Paper
      variant="outlined"
      onClick={() => row.trip && onOpen(row.trip.id)}
      onKeyDown={(e) => { if (e.key === 'Enter' && row.trip) onOpen(row.trip.id); }}
      role={row.trip ? 'button' : undefined}
      tabIndex={row.trip ? 0 : undefined}
      sx={{
        p: 1.25,
        cursor: row.trip ? 'pointer' : 'default',
        // The same leading edge the rows use, so moving between the two views costs no relearning.
        borderLeft: 4,
        borderLeftColor: level === 'error' ? 'error.main' : level === 'warning' ? 'warning.main' : 'transparent',
      }}
    >
      <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
        <Typography variant="body2" fontWeight={700} noWrap title={row.vehicle}>{row.vehicle}</Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums', whiteSpace: 'nowrap' }}>
          {row.insideMs == null ? '—' : humanMs(row.insideMs)}
        </Typography>
      </Stack>
      {!!row.driver.name && (
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {row.driver.mobile ? `${row.driver.name} · ${row.driver.mobile}` : row.driver.name}
        </Typography>
      )}
      {!!row.transporter && (
        <Typography variant="caption" color="text.secondary" display="block" noWrap>{row.transporter}</Typography>
      )}
      {/* What the lorry is carrying, which is how a desk refers to it on the telephone - a plate
          identifies the vehicle, the challan identifies the job somebody is ringing about. */}
      {!!row.trip && (
        <Typography variant="caption" color="text.secondary" display="block" noWrap>
          {row.trip.shipmentId || `Q-${row.trip.id}`}
        </Typography>
      )}
      {row.attention && (
        <Tooltip title={row.attention.detail}>
          <Chip
            size="small"
            sx={{ mt: 0.5 }}
            color={level === 'error' ? 'error' : 'warning'}
            variant="outlined"
            label={row.attention.label}
          />
        </Tooltip>
      )}
    </Paper>
  );
};

const AreaBoard = ({ areas, rows, onOpen }) => {
  // Only the areas the current filters still allow, so the board and the list never disagree about
  // what is being looked at.
  const shown = areas.filter((a) => rows.some((r) => String(r.areaId) === String(a.id)));
  if (!shown.length) return null;
  return (
    <Box sx={{
      display: 'grid',
      gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(260px, 1fr))' },
      gap: 2,
      alignItems: 'start',
    }}
    >
      {shown.map((area) => {
        // Longest inside first, as everywhere else here: the lorry that has been standing longest is
        // the one somebody needs to do something about.
        const inside = rows.filter((r) => String(r.areaId) === String(area.id));
        const needing = inside.filter((r) => r.attention).length;
        return (
          <Stack key={area.id} gap={1}>
            <Box>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline" gap={1}>
                <Typography variant="subtitle2" fontWeight={700} noWrap title={area.name}>{area.name}</Typography>
                <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>{inside.length}</Typography>
              </Stack>
              <Typography variant="caption" color="text.secondary" display="block">
                {areaTypeLabel(area.type)}
              </Typography>
              <Typography
                variant="caption"
                display="block"
                color={needing ? 'error.main' : 'success.main'}
                fontWeight={needing ? 700 : 400}
              >
                {attentionText(needing)}
              </Typography>
            </Box>
            {/* Tall columns scroll rather than pushing the next area off the bottom of the page, so
                a busy yard stays one screen wide however many lorries are in one corner of it. */}
            <Stack gap={1} sx={{ maxHeight: 460, overflowY: 'auto', pr: 0.5 }}>
              {inside.map((row) => <Card key={row.key} row={row} onOpen={onOpen} />)}
            </Stack>
          </Stack>
        );
      })}
    </Box>
  );
};

export default AreaBoard;
